import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { api } from "lib/api";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import { toast } from "react-toastify";
import { FieldSelect } from "@knowtex-ai/components";

const MODEL_TYPE_OPTIONS = [
  { value: "English", label: "English" },
  { value: "Multilingual", label: "Multilingual" },
];

interface ModelTypeSelectorProps {
  currentModelType?: string | null;
}

export default function ModelTypeSelector({ currentModelType }: ModelTypeSelectorProps) {
  const gate = useFeatureGate("model-type-selector");
  const [selectedModelType, setSelectedModelType] = useState<string | null>(
    currentModelType ?? null
  );

  useEffect(() => {
    if (currentModelType) {
      setSelectedModelType(currentModelType);
    }
  }, [currentModelType]);

  const mutation = useMutation({
    mutationFn: async (modelType: string) => {
      return api.put("/dashboard/model-type", { modelType }) as Promise<{
        modelType: string;
      }>;
    },
    onSuccess: (data) => {
      setSelectedModelType(data.modelType);
      toast.success("Model type updated");
    },
    onError: (error: Error) => {
      Sentry.captureException(error, {
        extra: { context: "ModelTypeSelector update" },
      });
      toast.error("Failed to update model type");
    },
  });

  if (gate.isLoading || !gate.hasFeature()) {
    return null;
  }

  const handleChange = (value: string | null) => {
    if (value && value !== selectedModelType) {
      setSelectedModelType(value);
      mutation.mutate(value);
    }
  };

  return (
    <div className="mt-5">
      <h2 className="text-accent1 text-2xl font-bold">Model Type</h2>
      <p className="text-light-grey italic text-sm mb-3">
        Select the model type for note generation.
      </p>
      <div id="model_type_selector" className="w-fit">
        <FieldSelect
          items={MODEL_TYPE_OPTIONS}
          value={selectedModelType}
          onChange={handleChange}
          placeholder="Select model type"
          disabled={mutation.isPending}
          aria-label="Model type"
          triggerMinWidth="200px"
        />
      </div>
    </div>
  );
}
